import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import { useLanguage } from "../../../context/LanguageContext";

const SectionJIECMonitoring = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    j1: "",
    j2: "",
    j3: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uiMessage, setUiMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['j1', 'j2', 'j3'];
    
    requiredFields.forEach(field => {
      if (!form[field]) {
        newErrors[field] = t.global.err_a1;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to convert YES/NO to integer
  const convertYesNoToInt = (value) => {
    return value === t.global.yes ? 1 : value === t.global.no ? 2 : "";
  };

  /* ================================
     SAVE & SUBMIT SECTION J
  ================================ */
  const handleSubmit = async () => {
    setUiMessage(""); // Reset message
    if (!validateForm()) {
      setUiMessage(t.global.alert_fix_errors);
      return;
    }

    try {
      setIsSubmitting(true);
      const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
      surveyData.sectionJ = form;
      localStorage.setItem("surveyData", JSON.stringify(surveyData));

      const completedSections = JSON.parse(localStorage.getItem("completedSections") || "{}");
      completedSections.j = true;
      localStorage.setItem("completedSections", JSON.stringify(completedSections));

      const payload = new FormData();
      payload.append("municipality_id", state?.municipality_id || "1");
      payload.append("ward_id", state?.ward_id || "5");
      payload.append("district_id", state?.district_id || "10");
      payload.append("lattitude_longitude", state?.lattitude_longitude || "22.5726,88.3639");
      payload.append("survey_canteen_type", "kiosk");
      payload.append("program_name", "demo kiosk");
      payload.append("current_section", "iec");
      payload.append("kiosk_location_fulladdress", state?.address || "yeWWW");
      payload.append("reporting_date", "na");
      payload.append("area_id", state?.area_id || "2");
      payload.append("canteen_type_id", state?.canteen_type || "2");

      payload.append("iec_monitoring_J1", convertYesNoToInt(form.j1));
      payload.append("iec_monitoring_J2", convertYesNoToInt(form.j2));
      payload.append("iec_monitoring_J3", convertYesNoToInt(form.j3));

      await dispatch(createCanteen(payload));
      navigate("/survey/sections", { state });
    } catch (err) {
      setUiMessage(t.global.alert_server_error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const questions = [
    {
      field: "j1",
      text: t.modules.kiosk.sections.J.questions.posters,
      required: true
    },
    {
      field: "j2",
      text: t.modules.kiosk.sections.J.questions.onlineEntry,
      required: true
    },
    {
      field: "j3",
      text: t.modules.kiosk.sections.J.questions.couponReconciliation,
      required: true
    }
  ];

  const options = [t.global.yes, t.global.no];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{t.modules.kiosk.sections.J.title}</h2>
              
              <p className="text-gray-600">{t.modules.kiosk.sections.J.subtitle || "Information, education, communication and monitoring"}</p>
            </div>
            
            <div className="text-sm bg-orange-100 text-orange-800 px-3 py-1 rounded-full">
              {state?.canteenId ? `${t.global.canteen_id_label} ${state.canteenId}` : 
               state?.kioskId ? `${t.global.canteen_id_label} ${state.kioskId}` : t.global.na}
            </div>
          </div>

          <div className="space-y-6">
            {questions.map((q, idx) => (
              <div key={q.field} className={`border rounded-lg p-4 ${errors[q.field] ? 'border-red-500' : ''}`}>
                <label className="block font-medium mb-3">
                  {`J${idx + 1}. ${q.text}`}
                  <span className="text-red-500 ml-1">*</span>
                  {errors[q.field] && (
                    <span className="text-red-500 text-sm ml-2"> {errors[q.field]}</span>
                  )}
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {options.map(option => (
                    <label 
                      key={option} 
                      className={`flex items-center p-3 border rounded cursor-pointer ${
                        form[q.field] === option ? 'bg-blue-50 border-blue-300' : ''
                      }`}
                    >
                      <input 
                        type="radio" 
                        name={q.field} 
                        value={option} 
                        checked={form[q.field] === option} 
                        onChange={handleChange} 
                        className="mr-3 h-5 w-5" 
                      />
                      <span className="text-lg">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            {/* UI Alert Message - Appears above buttons */}
            {uiMessage && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg flex items-center animate-pulse">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {uiMessage}
              </div>
            )}

            {/* Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button 
                type="button"
                onClick={() => navigate("/survey/sections", { state })} 
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                {t.global.back_to_sections}
              </button>
              
              <button 
                type="button"
                onClick={handleSubmit} 
                // Button is disabled if any field is empty
                disabled={isSubmitting || !form.j1 || !form.j2 || !form.j3}
                className={`px-6 py-3 rounded-lg text-white transition font-bold ${
                  (!form.j1 || !form.j2 || !form.j3) 
                    ? "bg-green-400 text-gray-600 cursor-not-allowed" 
                    : "bg-green-600 hover:bg-green-700"
                }`}
              >
                {isSubmitting ? t.global.submitting : t.global.submit_section_j}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionJIECMonitoring;